"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

/**
 * The catalogue grid for the `infinite` product list mode (Manage → Design).
 *
 * ## Why the cards arrive as nodes rather than as data
 *
 * `ProductCard` is an async server component: it reads the request locale's
 * messages. A client grid that re-implemented the card would be a second card to
 * keep in step with the first, which is the drift `StoreHomePage`'s docblock
 * spends a paragraph refusing. So the server renders every card and hands them
 * here as a prop; this component only decides how many of them are mounted.
 *
 * ⚠️ It mounts a PREFIX, it does not hide a suffix. The first paint therefore
 * matches the first render on both sides and nothing flashes — the price is that
 * a client with no JavaScript, and anything that reads the initial HTML, sees
 * one chunk. That is the accepted cost of this mode: `pagination` keeps a
 * crawlable `?page=` chain and `all` emits the whole grid, so a merchant who
 * needs either has a setting for it.
 *
 * The button is not a fallback ornament. `IntersectionObserver` reveals on
 * scroll, which a keyboard-only shopper never performs, so the same action has
 * to be reachable by focus.
 */
export function InfiniteProductGrid({
	items,
	step,
	className,
}: {
	/** Every product card, already rendered on the server. */
	items: ReactNode[];
	/**
	 * How many more cards each reveal mounts.
	 *
	 * ⚠️ Passed in rather than defaulted from `CATALOG_PAGE_SIZE` here. This is a
	 * `"use client"` module, and importing the VALUE would pull `commerce/catalog`
	 * and its server-side graph into the storefront bundle; the caller is a server
	 * component and can read the constant for free.
	 */
	step: number;
	/** Grid classes, so the caller keeps one definition of the layout. */
	className: string;
}) {
	const t = useTranslations("shop");
	const [shown, setShown] = useState(() => Math.min(step, items.length));
	const sentinelRef = useRef<HTMLDivElement>(null);
	const done = shown >= items.length;

	// ⚠️ Re-subscribed on every reveal, `shown` included in the dependencies on
	// purpose. `IntersectionObserver` reports a CHANGE in intersection, and after a
	// reveal the sentinel is still inside the same 600px margin it was already
	// inside — so a single long-lived observer delivers one entry and then goes
	// quiet. Measured: the grid grew by exactly one chunk and stopped. A fresh
	// observer reports the current state on its first callback, which is what keeps
	// the reveal going while the shopper holds the page at the bottom.
	useEffect(() => {
		if (shown >= items.length) return;
		const el = sentinelRef.current;
		if (!el || typeof IntersectionObserver === "undefined") return;
		const io = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting))
					setShown((n) => Math.min(n + step, items.length));
			},
			// Reveal before the shopper reaches the end of the grid, so the next
			// cards are already mounted by the time they would have run out.
			{ rootMargin: "600px" },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [shown, items.length, step]);

	return (
		<>
			<div className={className}>{items.slice(0, shown)}</div>
			{/*
			  The reveal mounts cards with no other announcement, so a shopper on a
			  screen reader pressing the button below hears nothing come back. The
			  running count is what closes that loop; `shop.results` is the same
			  string the catalogue header prints, so it needs no new key and is
			  already plural-correct in both locales.
			*/}
			<p aria-live="polite" className="sr-only">
				{t("results", { count: shown })}
			</p>
			{!done && (
				<div ref={sentinelRef} className="mt-8 flex justify-center">
					<button
						type="button"
						className="btn btn-outline text-sm"
						onClick={() => setShown((n) => Math.min(n + step, items.length))}
					>
						{t("seeMore")}
					</button>
				</div>
			)}
		</>
	);
}
